import {
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

type PopoverAlign = 'center' | 'start' | 'end';
type PopoverSide = 'bottom' | 'top';

interface HoverPopoverProps {
    /** The trigger element (e.g. a Button). */
    children: ReactNode;
    /**
     * Popover body. When null/undefined the trigger renders bare (no wrapper),
     * so callers can pass optional content without branching.
     */
    content: ReactNode;
    /**
     * Preferred horizontal anchor of the panel relative to the trigger. Default
     * 'center'. When 'center' would push the panel off the left/right edge of
     * the viewport it auto-flips to 'start'/'end' to stay on-page; an explicit
     * 'start'/'end' is respected as-is.
     */
    align?: PopoverAlign;
    /**
     * Escape `overflow-hidden` clipping. By default the panel is absolutely
     * positioned and so is clipped by any `overflow-hidden` ancestor (e.g. a
     * trigger at the bottom edge of a `Card noPadding` — its downward panel is
     * cut to the row rect). Set this and the panel is positioned with `fixed`
     * viewport coordinates measured from the trigger instead, so it opens
     * downward *over* surrounding content rather than being clipped.
     */
    overlay?: boolean;
    /**
     * Render the wrapper and the panel as `span`s instead of `div`s, so the
     * popover can sit inside prose — a `<div>` inside a markdown `<p>` is
     * invalid HTML and React flags it. Layout is unchanged (the wrapper stays
     * `inline-flex`, the panel is forced back to `block`).
     */
    inline?: boolean;
    /** Extra classes for the panel — typically a width override (default w-72). */
    panelClassName?: string;
    /**
     * The side of the trigger that the panel opens on. Default 'bottom'. Use
     * 'top' for a trigger at the bottom edge of its scroll region, where a
     * panel below it is out of view.
     */
    side?: PopoverSide;
}

// Horizontal anchor for the in-flow (absolute) panel: both the edge and the
// counter-translate that centres/pins it on that edge.
const ALIGN: Record<PopoverAlign, string> = {
    center: 'left-1/2 -translate-x-1/2',
    start: 'left-0',
    end: 'right-0',
};

// Horizontal anchor for the overlay (fixed) panel: the `left` coordinate is the
// trigger anchor point (set inline); these classes only translate the panel so
// that point lands at its centre / left / right edge.
const OVERLAY_ALIGN: Record<PopoverAlign, string> = {
    center: '-translate-x-1/2',
    start: '',
    end: '-translate-x-full',
};

// Keep the panel this far from the viewport edge when deciding to flip.
const EDGE_MARGIN = 8;

// Gap between the trigger and the panel, mirroring the `mt-1.5` of the in-flow
// variant (6px). Used to offset the fixed `top` in overlay mode.
const GAP = 6;

// HoverPopover — the kit's uniform hover/focus popover. CSS-driven visibility (no
// library): a `group` wrapper reveals the panel on hover OR keyboard focus
// (`group-focus-within`), so it is reachable without a mouse. `pointer-events-none`
// keeps it a pure read-only hint.
//
// The only JS is positioning. The default in-flow panel is absolutely positioned
// and only needs edge-avoidance: when the preferred 'center' anchor would clip
// off the left/right of the viewport, it flips to 'start'/'end'. In `overlay`
// mode (for a trigger inside an `overflow-hidden` ancestor) the panel is instead
// positioned with `fixed` viewport coordinates measured from the trigger, so it
// escapes the clip and paints on top while still opening downward. Both measure
// on mount, on resize, on scroll, and each time the trigger is hovered/focused
// (cheap; the panel keeps layout while hidden via `invisible`).
export default function HoverPopover({
    children,
    content,
    align = 'center',
    overlay = false,
    inline = false,
    panelClassName,
    side = 'bottom',
}: HoverPopoverProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [resolved, setResolved] = useState<PopoverAlign>(align);
    // Fixed viewport coordinates for overlay mode; null until first measured.
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

    const recompute = useCallback(() => {
        const wrapper = wrapperRef.current;
        const panel = panelRef.current;
        if (!wrapper || !panel) return;

        const rect = wrapper.getBoundingClientRect();
        const vw = window.innerWidth;

        // Only the default 'center' anchor auto-flips; explicit anchors stand.
        let nextAlign = align;
        if (align === 'center') {
            const half = panel.offsetWidth / 2;
            const centre = rect.left + rect.width / 2;
            const overflowsRight = centre + half > vw - EDGE_MARGIN;
            const overflowsLeft = centre - half < EDGE_MARGIN;
            // Flip toward the side with room; if both overflow (panel wider than
            // the viewport allows either way), keep centred — nothing better.
            if (overflowsRight && !overflowsLeft) nextAlign = 'end';
            else if (overflowsLeft && !overflowsRight) nextAlign = 'start';
            else nextAlign = 'center';
        }
        setResolved(nextAlign);

        if (!overlay) return;
        // Anchor the fixed panel just below the trigger; OVERLAY_ALIGN translates
        // it horizontally so this `left` lands at its centre/left/right edge.
        const left =
            nextAlign === 'start'
                ? rect.left
                : nextAlign === 'end'
                  ? rect.right
                  : rect.left + rect.width / 2;
        setCoords({ top: side === 'top' ? rect.top - GAP : rect.bottom + GAP, left });
    }, [align, overlay, side]);

    useLayoutEffect(() => {
        if (!content) return;
        recompute();
        window.addEventListener('resize', recompute);
        // Fixed coordinates don't track scroll, so reflow on any scroll (capture
        // catches nested scroll containers, not just the window).
        if (overlay) window.addEventListener('scroll', recompute, true);
        return () => {
            window.removeEventListener('resize', recompute);
            if (overlay) window.removeEventListener('scroll', recompute, true);
        };
    }, [content, overlay, recompute]);

    if (!content) return <>{children}</>;

    // Dynamic px coordinates can't be expressed as utility classes; this inline
    // style is the positioning escape hatch for overlay mode only.
    const overlayStyle: CSSProperties | undefined =
        overlay && coords ? { top: coords.top, left: coords.left } : undefined;

    // Same element semantics either way — `span` only so the popover is legal
    // inside prose (see the `inline` prop). Both are structurally HTMLElement,
    // so the refs above type-check against either tag.
    const Wrapper = inline ? 'span' : 'div';
    const Panel = inline ? 'span' : 'div';

    return (
        <Wrapper
            ref={wrapperRef}
            className="group relative inline-flex"
            onPointerEnter={recompute}
            onFocus={recompute}
        >
            {children}
            <Panel
                ref={panelRef}
                role="tooltip"
                style={overlayStyle}
                className={cn(
                    'septima-panel pointer-events-none z-50 block w-72 p-3 text-left',
                    overlay
                        ? 'fixed'
                        : side === 'top'
                          ? 'absolute bottom-full mb-1.5'
                          : 'absolute top-full mt-1.5',
                    // The panel slides in from the side it opens on: a hair
                    // below its place for 'bottom', a hair above for 'top'.
                    // In overlay mode on 'top' the panel also lifts by its own
                    // height, because its `top` is the top edge of the trigger.
                    'invisible opacity-0',
                    overlay && side === 'top'
                        ? '-translate-y-[calc(100%+2px)] group-hover:-translate-y-full group-focus-within:-translate-y-full'
                        : side === 'top'
                          ? '-translate-y-0.5 group-hover:translate-y-0 group-focus-within:translate-y-0'
                          : 'translate-y-0.5 group-hover:translate-y-0 group-focus-within:translate-y-0',
                    'transition-[opacity,transform] duration-card-hover',
                    'group-hover:visible group-hover:opacity-100',
                    'group-focus-within:visible group-focus-within:opacity-100',
                    overlay ? OVERLAY_ALIGN[resolved] : ALIGN[resolved],
                    panelClassName,
                )}
            >
                {content}
            </Panel>
        </Wrapper>
    );
}
